import { Stack } from '@mui/material';
import { useEffect, useState } from 'react';

import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { ArrowCircleUpFilledIcon } from '@/components/icons/ArrowCircleUpFilledIcon';
import { ContextIcon } from '@/components/icons/ContextIcon';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { ChatComposer } from '@/modules/common/Chat/components/ChatComposer';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useIsConversationWorkflowTrackerInProgress } from '@/modules/workflowTrackers/hooks/useIsConversationWorkflowTrackerInProgress';
import { COLORS } from '@/styles/tokens/colors';
import { WorkflowTrackerKind } from '@/types/schema';

import { useHouseholdChatContext } from '../contexts/householdChat.context';
import type { DictationState } from '../hooks/useHouseholdChatDictation';
import { useSelectedHouseholdChatPersona } from '../hooks/useSelectedHouseholdChatPersona';
import { HouseholdChatComposerContextBar } from './HouseholdChatComposerContextBar';
import { HouseholdChatComposerLeadingActions } from './HouseholdChatComposerLeadingActions';
import { HouseholdChatDictationControl } from './HouseholdChatDictationControl';

export function HouseholdChatComposer() {
  const [dictationState, setDictationState] = useState<DictationState>('idle');
  const [message, setMessage] = useState('');
  const audioTranscriptionEnabled = useFeatureFlag(
    'household_chat_audio_transcription'
  );
  const {
    awaitingFirstToken,
    chatModesEnabled,
    chatPersona,
    conversationId,
    dismissViewedContext,
    error,
    hasDismissedViewedContext,
    isReconcilingSubscription,
    lastUserMessage,
    messagesLoading,
    pendingInteractiveMessage,
    personaSelectorEnabled,
    restoreViewedContext,
    sendMessage,
    setChatPersona,
    viewedContext,
  } = useHouseholdChatContext();

  const selectedPersona = useSelectedHouseholdChatPersona();
  // While a persona is applied, the chip and the placeholder name it. Fall
  // back to a generic name if its catalog entry is missing (the personas
  // query failed) so the chip — and its remove button — never disappears
  // while a persona is still applied to sends.
  const personaDisplayName = chatPersona
    ? (selectedPersona?.displayName ?? 'Agent')
    : null;

  // A pending interactive message blocks the conversation: the user answers
  // through the card, not the composer.
  const isBlockedOnInteractiveMessage = !!pendingInteractiveMessage;
  // The diagram generation for this conversation changes the diagram that the
  // next message describes. Wait for the generation to finish.
  const isGeneratingDiagram = useIsConversationWorkflowTrackerInProgress(
    conversationId,
    WorkflowTrackerKind.DiagramGeneration
  );
  const isConversationUnavailable =
    isGeneratingDiagram ||
    messagesLoading ||
    isReconcilingSubscription ||
    isBlockedOnInteractiveMessage;
  const dictationBusy = dictationState !== 'idle';
  const isRecording = dictationState === 'recording';
  const showContextBar =
    isBlockedOnInteractiveMessage || !!viewedContext || !!personaDisplayName;
  const showLeadingActions = chatModesEnabled || personaSelectorEnabled;

  // Restore a failed send only if the user has not started another draft.
  // Incoming results and error recovery must not clear the current draft.
  useEffect(() => {
    if (error && lastUserMessage) {
      setMessage((currentMessage) => currentMessage || lastUserMessage);
    }
  }, [error, lastUserMessage]);

  const handleSubmit = async (nextMessage: string) => {
    setMessage('');
    await sendMessage(nextMessage);
  };

  const appendTranscript = (transcript: string) => {
    if (!transcript) {
      return;
    }
    setMessage((currentMessage) => {
      // Add one space between existing text and the transcript unless the
      // existing text already ends with a space.
      const separator =
        currentMessage && !currentMessage.endsWith(' ') ? ' ' : '';
      return `${currentMessage}${separator}${transcript}`;
    });
  };

  const contextAction =
    viewedContext || hasDismissedViewedContext ? (
      // The tooltip wraps the button in a span, so hover still surfaces the
      // label while the button is disabled.
      <Tooltip placement="top" title="Use page as context">
        <IconButton
          icon={ContextIcon}
          ariaLabel="Use page as context"
          variant="transparent"
          size="xs"
          disabled={!hasDismissedViewedContext}
          sx={{
            display: 'flex',
            width: 20,
            height: 20,
            minWidth: 20,
            p: 0,
            borderRadius: '4px',
            '&:hover': {
              bgcolor: COLORS.GRAY[100],
              borderColor: 'transparent',
              color: COLORS.TEXT.PRIMARY,
            },
            // Only tint the enabled state; leave the disabled state to the
            // variant's disabled gray.
            color: hasDismissedViewedContext ? COLORS.GRAY[500] : undefined,
          }}
          onClick={restoreViewedContext}
        />
      </Tooltip>
    ) : undefined;

  return (
    <ChatComposer
      buttonLabel="Ask"
      contextBar={
        showContextBar ? (
          <HouseholdChatComposerContextBar
            dismissViewedContext={dismissViewedContext}
            isBlockedOnInteractiveMessage={isBlockedOnInteractiveMessage}
            onRemovePersona={() => setChatPersona(null)}
            personaDisplayName={personaDisplayName}
            viewedContext={viewedContext}
          />
        ) : undefined
      }
      // Lock the composer while history or reconnect recovery loads, and while
      // an interactive message is awaiting the user's answer.
      disabled={isConversationUnavailable}
      leadingAction={
        <Stack spacing={1} minWidth={0}>
          {!isRecording && showLeadingActions && (
            <HouseholdChatComposerLeadingActions
              chatModesEnabled={chatModesEnabled}
              personaSelectorEnabled={personaSelectorEnabled}
            />
          )}
        </Stack>
      }
      // Give the household composer a taller, two-line typing area. Sizing the
      // entry area in rows keeps the typing space stable regardless of the
      // toolbar or other composer chrome.
      minRows={2}
      // Shown whenever the page has a viewed context, but restoring is a
      // no-op while the chip is attached, so the button is disabled until
      // the user dismisses the chip.
      trailingAction={
        <Stack direction="row" alignItems="center" gap={1}>
          {!isRecording && contextAction}
          {audioTranscriptionEnabled && (
            <HouseholdChatDictationControl
              disabled={isConversationUnavailable}
              onStateChange={setDictationState}
              onTranscript={appendTranscript}
            />
          )}
        </Stack>
      }
      onSubmit={handleSubmit}
      onValueChange={setMessage}
      placeholder="Ask Luminary about this household"
      testId="household-chat-composer"
      toolbar={({
        leadingAction,
        trailingAction,
        onSubmit,
        submitDisabled,
        testId,
      }) => (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%', px: 1, pb: 1 }}
        >
          {leadingAction ?? <span />}
          <Stack direction="row" alignItems="center" gap={1}>
            {trailingAction}
            <IconButton
              icon={ArrowCircleUpFilledIcon}
              iconProps={{ size: 24, 'aria-hidden': true }}
              ariaLabel="Ask Luminary"
              data-testid={testId ? `${testId}-submit` : undefined}
              variant="transparent"
              size="xs"
              disabled={submitDisabled}
              onClick={onSubmit}
              sx={{
                width: 24,
                height: 24,
                minWidth: 24,
                p: 0,
                color: message.trim()
                  ? COLORS.BRAND.ORANGE
                  : COLORS.TEXT.DISABLED,
                '&:hover': {
                  bgcolor: 'transparent',
                  borderColor: 'transparent',
                  color: message.trim()
                    ? COLORS.ORANGE[600]
                    : COLORS.TEXT.DISABLED,
                },
                '&.Mui-disabled': { color: COLORS.TEXT.DISABLED },
              }}
            />
          </Stack>
        </Stack>
      )}
      submitDisabled={
        awaitingFirstToken || dictationBusy || isConversationUnavailable
      }
      value={message}
    />
  );
}
